/**
 * OrderDetailsTable Component
 * Main reusable component for displaying order details with auto-hide columns and conditional styling
 */

import React, { useMemo, Fragment } from 'react';
import type { OrderDetailsTableProps } from '../../types/components';
import { getVisibleColumns } from '../../utils/orderTableHelpers';
import { DEFAULT_ORDER_COLUMNS, DEFAULT_ORDER_TABLE_CONFIG } from '../../utils/orderTableConfig';
import { OrderTableHeader } from './OrderTableHeader';
import { OrderTableRow } from './OrderTableRow';
import { LoadingSpinner } from './LoadingSpinner';
import { useGetGlassValuesDropdownQuery } from '../../store/slices/apiSlice';
import { isOrderGAP } from '../../utils/gapDetection';
import {
  buildJobTotalsMap,
  calculateOverallTotals,
  formatJobGroupHeader,
  formatOverallTotalsFooter,
} from '../../utils/productionLineTotals';

export const OrderDetailsTable: React.FC<OrderDetailsTableProps> = ({
  orders,
  loading = false,
  config = {},
  mode = 'view',
  rackContext,
  onEdit,
  onDelete,
  customActions = [],
  className = '',
  onTriggerCompleteDialog
}) => {
  // Merge provided config with defaults; unit price only on job edit orders table
  const tableConfig = useMemo(() => {
    const rawColumns = config.columns || DEFAULT_ORDER_COLUMNS;
    const columns =
      rackContext === 'orders_edit'
        ? rawColumns
        : rawColumns.filter((c) => c.key !== 'unit_price');
    return {
      ...DEFAULT_ORDER_TABLE_CONFIG,
      ...config,
      columns,
      features: {
        ...DEFAULT_ORDER_TABLE_CONFIG.features,
        ...config.features,
      },
      styling: {
        ...DEFAULT_ORDER_TABLE_CONFIG.styling,
        ...config.styling,
      },
    };
  }, [config, rackContext]);

  // Calculate visible columns based on data availability
  const visibleColumns = useMemo(() => {
    const cols = !tableConfig.features?.autoHideEmptyColumns 
      ? tableConfig.columns 
      : getVisibleColumns(orders, tableConfig.columns);
    
    return cols;
  }, [orders, tableConfig.columns, tableConfig.features?.autoHideEmptyColumns]);

  // Determine if actions should be shown
  const showActions = mode !== 'view' || customActions.length > 0;
  const showJobGroupTotals = tableConfig.features?.jobGroupTotals ?? false;

  const jobTotalsMap = useMemo(
    () => (showJobGroupTotals ? buildJobTotalsMap(orders) : new Map()),
    [orders, showJobGroupTotals]
  );

  const overallTotals = useMemo(
    () => (showJobGroupTotals ? calculateOverallTotals(orders) : null),
    [orders, showJobGroupTotals]
  );

  const totalColSpan = visibleColumns.length + (showActions ? 1 : 0);

  // Fetch glass values for GAP detection (Condition Two needs lookup when *_is_arrival fields aren't available)
  const { data: glassValuesData } = useGetGlassValuesDropdownQuery({});
  
  // Create lookup map from glass values data
  const glassValuesLookup = useMemo(() => {
    if (glassValuesData?.results) {
      const lookup = new Map<string, { is_arrival: boolean; is_cutting: boolean }>();
      glassValuesData.results.forEach((gv) => {
        // Ensure key is a string (glass value ID)
        const key = String(gv.value);
        lookup.set(key, { is_arrival: gv.is_arrival, is_cutting: gv.is_cutting });
      });
      return lookup;
    }
    return undefined;
  }, [glassValuesData]);

  // Loading state
  if (loading) {
    return (
      <div className={`${className} flex justify-center py-8`}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Empty state
  if (!orders || orders.length === 0) {
    return (
      <div className={`${className} text-center py-8 text-gray-500`}>
        <div className="text-sm">No orders available</div>
      </div>
    );
  }

  // Table classes
  const tableClasses = [
    tableConfig.styling?.tableClass || 'min-w-full border text-sm',
    'border-collapse'
  ].join(' ');

  const headerClasses = tableConfig.styling?.headerClass || '';
  const bodyClasses = tableConfig.styling?.bodyClass || 'bg-white divide-y divide-gray-200';

  return (
    <div className={`${className} overflow-x-auto`}>
      <table className={tableClasses}>
        <OrderTableHeader
          columns={visibleColumns}
          showActions={showActions}
          className={headerClasses}
        />
        <tbody className={bodyClasses}>
          {orders.map((order) => {
            // Apply row-level styling
            // ALWAYS check for GAP first (highest priority) with lookup support
            const rowClassNames: string[] = [];
            
            // GAP orders get yellow background (highest priority)
            if (isOrderGAP(order, glassValuesLookup)) {
              rowClassNames.push('bg-yellow-100');
            }
            
            // Then apply custom rowClass if provided (will be merged with GAP styling)
            if (tableConfig.styling?.rowClass) {
              const customClass = tableConfig.styling.rowClass(order);
              if (customClass) {
                rowClassNames.push(customClass);
              }
            }
            
            const rowClassName = rowClassNames.filter(Boolean).join(' ');
            const isFirstOrderFromJob = Boolean((order as { is_first_order_from_job?: boolean }).is_first_order_from_job);
            const jobId = (order as { job_id?: string }).job_id;
            const jobTotals = showJobGroupTotals && jobId ? jobTotalsMap.get(jobId) : undefined;
            
            return (
              <Fragment key={order.id}>
                {showJobGroupTotals && isFirstOrderFromJob && jobTotals && (
                  <tr className="bg-gray-100 border-t border-gray-300">
                    <td
                      colSpan={totalColSpan}
                      className="p-2 text-sm font-semibold text-gray-700"
                    >
                      {formatJobGroupHeader(jobTotals)}
                    </td>
                  </tr>
                )}
                <OrderTableRow
                  order={order}
                  columns={visibleColumns}
                  actions={customActions}
                  onEdit={mode === 'edit' ? onEdit : undefined}
                  onDelete={mode === 'edit' ? onDelete : undefined}
                  rowClassName={rowClassName}
                  allOrders={orders}
                  onTriggerCompleteDialog={onTriggerCompleteDialog}
                  tableMode={mode}
                  rackContext={rackContext ?? config.rackContext}
                />
              </Fragment>
            );
          })}
        </tbody>
        {showJobGroupTotals && overallTotals && (
          <tfoot>
            <tr className="bg-gray-50 border-t-2 border-gray-300">
              <td
                colSpan={totalColSpan}
                className="p-2 text-sm font-semibold text-gray-800"
              >
                {formatOverallTotalsFooter(overallTotals)}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

OrderDetailsTable.displayName = 'OrderDetailsTable';
